import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { createDealSchema, type ConvertLeadInput, type CreateDealInput, type LeadResponse } from '@orion/contracts';
import { Button, Card, Input } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { useCompaniesQuery } from '../companies/api.js';
import { useContactsQuery } from '../contacts/api.js';
import { useConvertLead, useLeadsQuery } from '../leads/api.js';
import { useCreateDeal, usePipelinesQuery } from './api.js';

/** Picking an existing lead here routes the submit through the same
 * `POST /leads/:id/convert` the Lead-detail "Convert" flow already uses
 * (`ConvertLeadModal.tsx`) instead of `POST /deals` directly — company/
 * contact match-or-create and the lead's own `convertedAt`/
 * `convertedOpportunityId` bookkeeping all come along for free, rather than
 * inventing a second, inconsistent way to link a lead to an opportunity. */
export function NewDealPage() {
  const navigate = useNavigate();
  const { data: pipelines } = usePipelinesQuery();
  const createDeal = useCreateDeal();
  const pipeline = pipelines?.[0];

  const [pickedLead, setPickedLead] = useState<LeadResponse | null>(null);
  const [leadPickerOpen, setLeadPickerOpen] = useState(false);
  const [leadQuery, setLeadQuery] = useState('');
  const { data: leadMatches } = useLeadsQuery({ q: leadQuery || undefined, pageSize: 5 });
  const leadCandidates = (leadMatches?.data ?? []).filter((l) => !l.convertedAt);

  const { data: companyMatches } = useCompaniesQuery({ q: pickedLead?.companyName ?? undefined, pageSize: 1 });
  const { data: contactMatches } = useContactsQuery({ q: pickedLead?.email ?? pickedLead?.fullName, pageSize: 1 });
  const convertLead = useConvertLead(pickedLead?.id ?? '');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateDealInput>({
    resolver: zodResolver(createDealSchema),
    values: pipeline ? { pipelineId: pipeline.id, stageId: pipeline.stages[0]?.id ?? '', currency: 'USD', customFields: {}, name: '', amount: 0 } : undefined,
  });

  const pickLead = (lead: LeadResponse) => {
    setPickedLead(lead);
    setLeadPickerOpen(false);
    setLeadQuery('');
    setValue('name', `${lead.companyName ?? lead.fullName} — New business`);
  };

  const onSubmit = async (input: CreateDealInput) => {
    if (pickedLead) {
      const companyMatch = companyMatches?.data[0];
      const contactMatch = contactMatches?.data[0];
      const convertInput: ConvertLeadInput = {
        company: companyMatch ? { mode: 'link', companyId: companyMatch.id } : pickedLead.companyName ? { mode: 'create' } : undefined,
        contact: contactMatch ? { mode: 'link', contactId: contactMatch.id } : { mode: 'create' },
        deal: {
          name: input.name,
          pipelineId: input.pipelineId,
          stageId: input.stageId,
          amount: input.amount,
          currency: input.currency,
          expectedCloseDate: input.expectedCloseDate,
          source: input.source,
        },
      };
      const result = await convertLead.mutateAsync(convertInput);
      await navigate({ to: '/deals/$dealId', params: { dealId: result.opportunityId! } });
      return;
    }
    const deal = await createDeal.mutateAsync(input);
    await navigate({ to: '/deals/$dealId', params: { dealId: deal.id } });
  };

  if (!pipeline) return <PageHeader title="New opportunity" />;

  return (
    <>
      <PageHeader title="New opportunity" />
      <main className="flex flex-1 justify-center p-7">
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex w-full max-w-lg flex-col gap-4">
          {pickedLead ? (
            <div className="flex items-center justify-between rounded-control border border-primary-border bg-primary-soft px-3 py-2 text-sm">
              <span>
                Starting from lead <strong className="font-semibold">{pickedLead.fullName}</strong>
                {pickedLead.companyName ? ` · ${pickedLead.companyName}` : ''}
              </span>
              <button type="button" onClick={() => setPickedLead(null)} className="text-xs font-semibold text-primary-text hover:underline">
                Remove
              </button>
            </div>
          ) : leadPickerOpen ? (
            <Card className="p-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Start from an existing lead</h3>
              <input
                autoFocus
                value={leadQuery}
                onChange={(e) => setLeadQuery(e.target.value)}
                type="search"
                placeholder="Search by name, company, email…"
                className="h-9 w-full rounded-control border border-border-strong bg-surface px-3 text-sm"
              />
              {leadQuery && (
                <div className="mt-3 flex flex-col gap-2">
                  {leadCandidates.length === 0 && <p className="text-sm text-text-muted">No matching leads.</p>}
                  {leadCandidates.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => pickLead(l)}
                      className="flex flex-col gap-0.5 rounded-control border border-border px-3 py-2 text-left text-sm hover:bg-surface-subtle"
                    >
                      <span className="truncate font-medium text-ink">{l.fullName}</span>
                      <span className="truncate text-xs text-text-muted">
                        {l.companyName ?? 'No company'} · {l.email ?? 'no email'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <Button size="sm" variant="ghost" className="mt-3" onClick={() => setLeadPickerOpen(false)}>
                Cancel
              </Button>
            </Card>
          ) : (
            <button type="button" onClick={() => setLeadPickerOpen(true)} className="self-start text-xs font-semibold text-primary-text hover:underline">
              Start from an existing lead
            </button>
          )}

          <Input label="Opportunity name" error={errors.name?.message} {...register('name')} />
          <Input label="Amount (USD)" type="number" step="0.01" error={errors.amount?.message} {...register('amount', { valueAsNumber: true })} />
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Stage
            <select className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal" {...register('stageId')}>
              {pipeline.stages.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <Input label="Expected close date" type="date" error={errors.expectedCloseDate?.message} {...register('expectedCloseDate')} />
          <Input label="Source" placeholder="e.g. Referral, Website, Upwork" error={errors.source?.message} {...register('source')} />
          <div className="mt-2 flex justify-end">
            <Button type="submit" loading={isSubmitting || convertLead.isPending}>
              Save opportunity
            </Button>
          </div>
        </form>
      </main>
    </>
  );
}
